import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Alert,
  CircularProgress,
  Button,
  LinearProgress,
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  AttachMoney,
  Home,
  Assessment,
  Notifications,
} from '@mui/icons-material';
import {
  Line,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { fetchWithCacheFallback } from '../services/api-client.service';
import { ResponsiveChartBox } from '../components/common/ResponsiveChartBox';

interface DashboardMetrics {
  financialSummary: {
    monthlyRevenue: number;
    monthlyExpenses: number;
    netProfit: number;
    profitMargin: number;
  };
  operationalMetrics: {
    currentOccupancy: number;
    occupancyRate: number;
    availableBeds: number;
    pendingCheckouts: number;
  };
  collectionMetrics: {
    monthlyCollections: number;
    outstandingDues: number;
    collectionEfficiency: number;
    overdueAccounts: number;
  };
  trends: {
    revenueChange: number;
    expenseChange: number;
    occupancyChange: number;
    collectionChange: number;
  };
  monthlyTrends?: Array<{ month: string; revenue: number; expenses: number; profit: number }>;
  alerts: Array<{
    type: 'success' | 'warning' | 'error' | 'info';
    message: string;
  }>;
}

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const [metrics, setMetrics] = useState<DashboardMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const { data } = await fetchWithCacheFallback<{ success: boolean; data: DashboardMetrics }>(
        '/api/reports/dashboard'
      );
      const dashboardData = data.data ?? data;
      setMetrics(dashboardData as DashboardMetrics);
    } catch (err) {
      setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => `₹${amount.toLocaleString()}`;

  const formatPercentage = (value: number) => `${value.toFixed(1)}%`;

  const getTrendIcon = (value: number) => {
    return value >= 0 ? <TrendingUp color="success" /> : <TrendingDown color="error" />;
  };

  const getTrendColor = (value: number) => {
    return value >= 0 ? 'success.main' : 'error.main';
  };

  const FinancialSummaryCards = () => {
    if (!metrics) return null;

    const { financialSummary, trends } = metrics;

    return (
      <Grid container spacing={3}>
        <Grid item xs={12} sm={6} md={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <AttachMoney color="primary" sx={{ mr: 1 }} />
                <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600 }}>
                  Monthly Revenue
                </Typography>
              </Box>
              <Typography variant="h4" color="primary.main" gutterBottom sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                {formatCurrency(financialSummary.monthlyRevenue)}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {getTrendIcon(trends.revenueChange)}
                <Typography
                  variant="body2"
                  color={getTrendColor(trends.revenueChange)}
                  sx={{ ml: 0.5 }}
                >
                  {formatPercentage(Math.abs(trends.revenueChange))} from last month
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Assessment color="secondary" sx={{ mr: 1 }} />
                <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600 }}>
                  Monthly Expenses
                </Typography>
              </Box>
              <Typography variant="h4" color="secondary.main" gutterBottom sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                {formatCurrency(financialSummary.monthlyExpenses)}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {getTrendIcon(trends.expenseChange)}
                <Typography
                  variant="body2"
                  color={getTrendColor(trends.expenseChange)}
                  sx={{ ml: 0.5 }}
                >
                  {formatPercentage(Math.abs(trends.expenseChange))} from last month
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <TrendingUp color="success" sx={{ mr: 1 }} />
                <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600 }}>
                  Net Profit
                </Typography>
              </Box>
              <Typography variant="h4" color="success.main" gutterBottom sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                {formatCurrency(financialSummary.netProfit)}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Margin: {formatPercentage(financialSummary.profitMargin)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Home color="info" sx={{ mr: 1 }} />
                <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600 }}>
                  Occupancy Rate
                </Typography>
              </Box>
              <Typography variant="h4" color="info.main" gutterBottom sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                {formatPercentage(metrics.operationalMetrics.occupancyRate)}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {metrics.operationalMetrics.currentOccupancy} of 45 beds occupied
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    );
  };

  const OperationalMetricsCard = () => {
    if (!metrics) return null;

    const { operationalMetrics, collectionMetrics } = metrics;

    return (
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Operational Overview
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2">Occupancy Rate</Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {formatPercentage(operationalMetrics.occupancyRate)}
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={operationalMetrics.occupancyRate}
                  sx={{ height: 8, borderRadius: 4 }}
                />
              </Box>
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2">Collection Efficiency</Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {formatPercentage(collectionMetrics.collectionEfficiency)}
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={collectionMetrics.collectionEfficiency}
                  color="success"
                  sx={{ height: 8, borderRadius: 4 }}
                />
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Box textAlign="center">
                    <Typography variant="h3" color="primary.main">
                      {operationalMetrics.availableBeds}
                    </Typography>
                    <Typography variant="caption">Available Beds</Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box textAlign="center">
                    <Typography variant="h3" color="warning.main">
                      {collectionMetrics.overdueAccounts}
                    </Typography>
                    <Typography variant="caption">Overdue Accounts</Typography>
                  </Box>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    );
  };

  const formatMonthLabel = (monthStr: string) => {
    const [y, m] = monthStr.split('-').map(Number);
    const d = new Date(y, m - 1, 1);
    return d.toLocaleDateString('en-IN', { month: 'short' });
  };

  const RevenueChart = () => {
    const rawData = metrics?.monthlyTrends ?? [];
    const data = rawData.map((r) => ({
      month: formatMonthLabel(r.month),
      revenue: r.revenue,
      expenses: r.expenses,
      profit: r.profit,
    }));

    return (
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Revenue & Expense Trends (6 Months)
          </Typography>
          <ResponsiveChartBox>
            <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={data}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis />
              <Tooltip formatter={(value: number) => [formatCurrency(value), '']} />
              <Legend />
              <Area
                type="monotone"
                dataKey="revenue"
                stackId="1"
                stroke="#8884d8"
                fill="#8884d8"
                name="Revenue"
              />
              <Area
                type="monotone"
                dataKey="expenses"
                stackId="2"
                stroke="#82ca9d"
                fill="#82ca9d"
                name="Expenses"
              />
              <Line
                type="monotone"
                dataKey="profit"
                stroke="#ff7300"
                strokeWidth={3}
                name="Profit"
              />
            </AreaChart>
          </ResponsiveContainer>
          </ResponsiveChartBox>
        </CardContent>
      </Card>
    );
  };

  const AlertsCard = () => {
    if (!metrics) return null;

    return (
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Notifications color="primary" sx={{ mr: 1 }} />
            <Typography variant="h6">Recent Alerts</Typography>
          </Box>
          <Box>
            {metrics.alerts.map((alert, index) => (
              <Alert key={index} severity={alert.type} sx={{ mb: 1 }}>
                {alert.message}
              </Alert>
            ))}
          </Box>
        </CardContent>
      </Card>
    );
  };

  const QuickActionsCard = () => (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Quick Actions
        </Typography>
        <Grid container spacing={1}>
          <Grid item xs={12} sm={6}>
            <Button fullWidth variant="outlined" sx={{ mb: 1, minHeight: 44 }} onClick={() => navigate('/residents')}>
              Add Resident
            </Button>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Button fullWidth variant="outlined" sx={{ mb: 1, minHeight: 44 }} onClick={() => navigate('/payments')}>
              Record Payment
            </Button>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Button fullWidth variant="outlined" sx={{ mb: 1, minHeight: 44 }} onClick={() => navigate('/expenses')}>
              Add Expense
            </Button>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Button fullWidth variant="outlined" sx={{ mb: 1, minHeight: 44 }} onClick={() => navigate('/payments')}>
              Send Reminder
            </Button>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box sx={{ minWidth: 0, maxWidth: '100%', overflow: 'hidden' }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom fontWeight={600}>
          Dashboard
        </Typography>
        <Typography variant="body1" color="textSecondary">
          Real-time overview of hostel operations and financial performance.
        </Typography>
      </Box>

      {/* Financial Summary Cards */}
      <Box sx={{ mb: 3 }}>
        <FinancialSummaryCards />
      </Box>

      <Grid container spacing={3}>
        {/* Revenue Chart */}
        <Grid item xs={12} lg={8}>
          <RevenueChart />
        </Grid>

        {/* Alerts */}
        <Grid item xs={12} lg={4}>
          <AlertsCard />
        </Grid>

        {/* Operational Metrics */}
        <Grid item xs={12} lg={8}>
          <OperationalMetricsCard />
        </Grid>

        {/* Quick Actions */}
        <Grid item xs={12} lg={4}>
          <QuickActionsCard />
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard;